import React, { useMemo } from 'react';
import './NTypography.css';

import { ReactNode, HTMLAttributes } from 'react';

export const typographyVariantOptions = [
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'b1',
  'b1-strong',
  'b1-link',
  'b2',
  'b2-strong',
  'b2-link',
  'b2-link-strong',
  'c1',
  'c1-strong',
  'c1-link',
  'c2',
  'c2-link',
  'c2-strong'
] as const;

export const typographyTagOptions = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'span'] as const;

export type TypographyTag = (typeof typographyTagOptions)[number];
export type TypographyVariant = (typeof typographyVariantOptions)[number];

export interface TypographyProps extends Omit<HTMLAttributes<HTMLElement>, 'className'> {
  /**
   * The variant to be used. Unless specified with the tag prop, the HTML tag used will be a <span> for any "c" variants, a <p> for any "b" variants, and the same h tag for any "h" variants.
   */
  variant: TypographyVariant;

  /**
   * The HTML tag to be used instead of the default mentioned above.
   */
  tag?: TypographyTag;

  /**
   * Setting this to true will remove the default margin.
   */
  noMargin?: boolean;

  /**
   * Additional CSS class names to apply to the component.
   */
  className?: string;

  /**
   * The content to be displayed within the typography component.
   */
  children?: ReactNode;
}

/**
 * Typography is essential in creating a cohesive and accessible design system.
 * It helps establish visual hierarchy, guide users through content, and reinforce the brand identity.
 * Proper use of typography ensures readability and enhances the overall user experience.
 */
const NTypography: React.FC<TypographyProps> = ({
  variant,
  tag,
  noMargin = false,
  className,
  children,
  ...restProps
}) => {
  // Utility function to check if a value is a valid typography tag
  const isTypographyTag = (value: string): value is TypographyTag => {
    return typographyTagOptions.includes(value as TypographyTag);
  };

  // Memoized tag calculation for performance optimization
  const computedTag = useMemo((): TypographyTag => {
    // If tag is explicitly provided and valid, use it
    if (tag !== undefined && isTypographyTag(tag)) {
      return tag;
    }

    // If variant itself is a valid tag (h1-h6), use it
    if (isTypographyTag(variant)) {
      return variant;
    }

    // Map body variants to paragraph tags
    if (
      ['b1', 'b1-strong', 'b1-link', 'b2', 'b2-strong', 'b2-link', 'b2-link-strong'].includes(
        variant
      )
    ) {
      return 'p';
    }

    // Map caption variants to span tags
    if (['c1', 'c1-strong', 'c1-link', 'c2', 'c2-strong', 'c2-link'].includes(variant)) {
      return 'span';
    }

    // Default fallback
    return 'span';
  }, [variant, tag]);

  // Memoized class names for performance optimization
  const computedClassName = useMemo(() => {
    const classes = ['n-typography', `n-typography-${variant}`];

    if (noMargin) {
      classes.push('n-typography-no-margin');
    }

    if (className) {
      classes.push(className);
    }

    return classes.join(' ');
  }, [variant, noMargin, className]);

  // Dynamic component creation using React.createElement for type safety
  return React.createElement(
    computedTag,
    {
      className: computedClassName,
      ...restProps
    },
    children
  );
};

export default NTypography;
